import React, { memo, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline } from '@atlaskit/primitives';

import { useAnalytics } from '../common/controller/analytics';
import { ErrorBoundary } from '../error-boundary';
import { KnowledgeBadge } from '../knowledge-card-components';
import { RelatedEntityType } from '../related-entities-api';

import { AITopicsFeedbackCollector } from './ai-topics-feedback-collector';
import { onRelatedEntityClick, onSurfaceShown } from './analytics';
import { messages } from './messages';
import { useRelatedTopics } from './use-related-topics';

type AITopicsArguments = {
	entityId: string;
	cloudId: string;
	principalEntityType: RelatedEntityType;
	userName: string;
	userAccountId: string;
	showFeedbackCollector?: boolean;
};

const AITopicsComponent = memo(
	({
		entityId,
		cloudId,
		principalEntityType,
		userName,
		userAccountId,
		showFeedbackCollector = true,
	}: AITopicsArguments) => {
		const intl = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { aiRelatedTopics, fetchRelatedTopics, showTopics } = useRelatedTopics();

		const onBadgeClickHandler = (topicId: string) => {
			fireAnalyticsEvent(
				onRelatedEntityClick({
					clickedEntityId: topicId,
					clickedEntityType: 'topic',
					principalEntityType,
					principalEntityId: entityId,
					location: principalEntityType === RelatedEntityType.USER ? 'userProfile' : 'endOfPage',
				}),
			);
		};

		const headerText =
			principalEntityType === RelatedEntityType.USER
				? intl.formatMessage(messages.ai_topics_surface_header_user)
				: intl.formatMessage(messages.ai_topics_surface_header_generic);
		const feedbackPromptText =
			principalEntityType === RelatedEntityType.USER
				? intl.formatMessage(messages.ai_topics_user_feedback_prompt)
				: intl.formatMessage(messages.ai_topics_page_feedback_prompt);
		const headingSize = principalEntityType === RelatedEntityType.USER ? 'small' : 'xsmall';

		useEffect(() => {
			fetchRelatedTopics({ entityId, principalEntityType, cloudId });
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		useEffect(() => {
			if (showTopics) {
				fireAnalyticsEvent(
					onSurfaceShown({
						principalEntityId: entityId,
						principalEntityType,
						relatedTopics: aiRelatedTopics.map((topic) => topic.id),
						location: principalEntityType === RelatedEntityType.USER ? 'userProfile' : 'endOfPage',
					}),
				);
			}
		}, [showTopics, aiRelatedTopics, entityId, fireAnalyticsEvent, principalEntityType]);

		return showTopics ? (
			<Box>
				<Inline space="space.050" alignBlock="center">
					<Heading size={headingSize}>{headerText}</Heading>
					<Lozenge appearance="new">
						{intl.formatMessage(messages.ai_topics_surface_header_beta_lozenge)}
					</Lozenge>
				</Inline>
				<Box paddingBlockStart="space.200">
					<Inline space="space.100" rowSpace="space.100" shouldWrap>
						{aiRelatedTopics.map((topic) => (
							<KnowledgeBadge
								key={topic.id}
								name={topic.name}
								url={`/wiki/topics/${topic.id}`}
								onBadgeClick={() => onBadgeClickHandler(topic.id)}
							/>
						))}
					</Inline>
				</Box>
				{showFeedbackCollector && (
					<AITopicsFeedbackCollector
						promptText={feedbackPromptText}
						userName={userName}
						userAccountId={userAccountId}
						entityId={entityId}
						principalEntityType={principalEntityType}
					/>
				)}
			</Box>
		) : null;
	},
);

export const AITopics = (props: AITopicsArguments) => (
	<ErrorBoundary>
		<AITopicsComponent {...props} />
	</ErrorBoundary>
);
